/* Shared UI kit + constants for the Merchant Webhooks tabs (Webhooks.js).
   Panel design language: soft-shadow cards, navy/muted type ramp, blue primary,
   tinted status pills — consistent with KCI / Order Details. Keep visual tokens
   here so all four webhook tabs stay in sync. */
import React from 'react';
import { Box, Typography } from '@mui/material';

export const T = {
  NAVY: '#212B36', MUTED: '#637381', BORDER: '#E7EAF0',
  BLUE: '#1565c0', BLUE_DARK: '#0d47a1', BLUE_BG: '#e8f1fc',
  GREEN: '#0F7B4F', GREEN_BG: '#E9F7EF',
  RED: '#C62828', RED_BG: '#FDECEA',
  AMBER: '#B45309', AMBER_BG: '#FFF7E6',
  PURPLE: '#6D28D9', PURPLE_BG: '#F5F3FF',
  TEAL: '#0F766E', TEAL_BG: '#E4F3F1',
  LIGHT: '#F7F9FC', WHITE: '#fff',
  FONT: 'Poppins, sans-serif',
};

export const cardSx = {
  bgcolor: T.WHITE, border: `1px solid ${T.BORDER}`, borderRadius: '14px',
  boxShadow: '0 1px 3px rgba(16,24,40,0.06)',
};

export const primaryBtnSx = {
  textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 2.5, py: 1,
  bgcolor: T.BLUE, color: '#fff', boxShadow: '0 2px 6px rgba(21,101,192,0.28)',
  '&:hover': { bgcolor: T.BLUE_DARK, boxShadow: '0 3px 10px rgba(21,101,192,0.36)' },
  '&.Mui-disabled': { bgcolor: '#B7C6DA', color: '#fff', boxShadow: 'none' },
};
export const softBtnSx = {
  textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2,
  bgcolor: T.BLUE_BG, color: T.BLUE, boxShadow: 'none',
  '&:hover': { bgcolor: T.BLUE, color: '#fff', boxShadow: 'none' },
};
export const dangerBtnSx = {
  textTransform: 'none', fontWeight: 600, borderRadius: '10px',
  color: T.RED, borderColor: '#F0C2C2', bgcolor: '#fff',
  '&:hover': { bgcolor: T.RED_BG, borderColor: T.RED },
};
export const ghostBtnSx = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px', py: 1.25,
  color: T.BLUE, border: `1.5px dashed rgba(21,101,192,0.4)`, bgcolor: 'rgba(232,241,252,0.5)',
  '&:hover': { bgcolor: T.BLUE_BG, borderColor: T.BLUE, borderStyle: 'dashed' },
};

// Rounded, clean TextField styling — spread into a TextField's sx.
export const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: 13.5, bgcolor: '#fff' },
  '& .MuiOutlinedInput-input': { fontSize: 13.5 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: T.BORDER },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#C4CDD5' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.BLUE },
};

export const FieldLabel = ({ children, required }) => (
  <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: T.MUTED, mb: 0.75, fontFamily: T.FONT }}>
    {children}{required && <Box component="span" sx={{ color: T.RED, ml: 0.3 }}>*</Box>}
  </Typography>
);

// Rounded section card with an optional grey header bar (title/subtitle/icon + action).
export const SectionCard = ({ title, subtitle, icon, action, children, sx, headerBg }) => (
  <Box sx={{ ...cardSx, overflow: 'hidden', ...sx }}>
    {(title || action) && (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, px: 2.5, py: 1.75, borderBottom: `1px solid ${T.BORDER}`, bgcolor: headerBg || T.LIGHT }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          {icon}
          <Box sx={{ minWidth: 0 }}>
            {title && <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.NAVY, fontFamily: T.FONT }}>{title}</Typography>}
            {subtitle && <Typography sx={{ fontSize: 12, color: T.MUTED, fontFamily: T.FONT }}>{subtitle}</Typography>}
          </Box>
        </Box>
        {action}
      </Box>
    )}
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

// Tinted rounded pill with an optional leading dot.
export const Pill = ({ tone, dot, children, sx }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1, py: 0.3, borderRadius: '999px', fontSize: 11, fontWeight: 700, lineHeight: 1.6, fontFamily: T.FONT, bgcolor: tone.bg, color: tone.fg, ...sx }}>
    {dot && <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: tone.fg }} />}
    {children}
  </Box>
);

// Page heading — bold navy title + muted subtitle, optional right-side action.
export const PageHeader = ({ title, subtitle, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
    <Box>
      <Typography sx={{ fontSize: 20, fontWeight: 700, color: T.NAVY, fontFamily: T.FONT, lineHeight: 1.2 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: 13, color: T.MUTED, mt: 0.25, fontFamily: T.FONT }}>{subtitle}</Typography>}
    </Box>
    {action}
  </Box>
);

// Centered empty / prompt state inside a dashed card.
export const EmptyState = ({ icon, title, subtitle }) => (
  <Box sx={{ ...cardSx, borderStyle: 'dashed', borderColor: '#D6DEEA', bgcolor: 'rgba(247,249,252,0.6)', px: 3, py: 6, textAlign: 'center' }}>
    {icon}
    <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.NAVY, mt: 1, fontFamily: T.FONT }}>{title}</Typography>
    {subtitle && <Typography sx={{ fontSize: 13, color: T.MUTED, mt: 0.5, fontFamily: T.FONT }}>{subtitle}</Typography>}
  </Box>
);

export const activeTone = (on) => (on ? { bg: T.GREEN_BG, fg: T.GREEN } : { bg: '#EEF1F5', fg: T.MUTED });

// Order lifecycle events a webhook can subscribe to. Single source of truth for
// all webhook tabs (was duplicated per file).
export const ORDER_STATUS_ENUM = [
  { remark: 'ORDER_PLACED', code: 1, description: 'Order Has Been Placed / Manifested on Courier Partner' },
  { remark: 'PICKUP_PENDING', code: 2, description: 'Pickup Pending' },
  { remark: 'PICKUP_FAILED', code: 3, description: 'Pickup Failed' },
  { remark: 'PICKED_UP', code: 4, description: 'Pickup Has Been Done' },
  { remark: 'INTRANSIT', code: 5, description: 'In Transit' },
  { remark: 'OUT_FOR_DELIVERY', code: 6, description: 'Shipment Out For Delivery' },
  { remark: 'NOT_SERVICEABLE', code: 7, description: 'Area For Delivery Is Not Servicable' },
  { remark: 'DELIVERED', code: 8, description: 'Shipment Delivered' },
  { remark: 'FAILED_DELIVERY', code: 9, description: 'Delivery Failed' },
  { remark: 'CANCELLED_ORDER', code: 10, description: 'Order Has Been Cancelled' },
  { remark: 'RTO_REQUESTED', code: 11, description: 'Rto For Shipment has been Requested' },
  { remark: 'RTO', code: 12, description: 'Marked As Return' },
  { remark: 'RTO_OUT_FOR_DELIVERY', code: 13, description: 'Shipment Is Out For Delivery For RTO' },
  { remark: 'RTO_DELIVERED', code: 14, description: 'RTO Delivered' },
  { remark: 'RTO_FAILED', code: 15, description: 'RTO Failed' },
  { remark: 'LOST', code: 16, description: 'Shipment is Lost' },
  { remark: 'DAMAGED', code: 17, description: 'Shipment is damaged' },
  { remark: 'SHIPMENT_DELAYED', code: 18, description: 'Shipment Is Delayed Or Misroute' },
  { remark: 'CONTACT_CUSTOMER_CARE', code: 19, description: 'Contact To The Customer Care' },
  { remark: 'SHIPMENT_HELD', code: 20, description: 'Shipment Is being held' },
  { remark: 'RTO_INTRANSIT', code: 21, description: 'RTO In Transit' },
  { remark: 'OUT_FOR_PICKUP', code: 25, description: 'Shipment Out For Pickup' },
  { remark: 'RTO_CONTACT_CUSTOMER_CARE', code: 26, description: 'RTO Contact Customer Care' },
  { remark: 'RTO_SHIPMENT_DELAY', code: 27, description: 'RTO Shipment Delayed' },
  { remark: 'AWB_REGISTERED', code: 28, description: 'AWB registered on proship' },
  { remark: 'EXCHANGE_PICKUP', code: 30, description: 'Exchange successful, Shipment delivered and picked up from the customer' },
  { remark: 'EXCHANGE_INTRANSIT', code: 31, description: 'Exchanged shipment In Transit to warehouse' },
  { remark: 'EXCHANGE_DELIVERED', code: 32, description: 'When an exchange shipment is delivered back' },
  { remark: 'MANIFESTED', code: 33, description: 'When shipment is manifested' },
  { remark: 'REACHED_AT_DESTINATION', code: 46, description: 'Shipment Reached At Destination' },
  { remark: 'SHIPPED', code: 47, description: 'Shipment Shipped' },
  { remark: 'MISSING', code: 48, description: 'Shipment is Missing' },
  { remark: 'DISPOSED_OFF', code: 49, description: 'Shipment is Disposed Off' },
  { remark: 'RETURN_ORDER_PLACED', code: 101, description: 'Return Order' },
];
export const ORDER_STATUS_OPTIONS = ORDER_STATUS_ENUM.map((e) => ({ label: e.description, value: e.remark }));

// Mirrors MerchantWebHook.PUSHFormatType on the backend. Determines the payload shape
// pushed to the merchant's endpoint; backend defaults to PROSHIP when absent.
export const FORMAT_TYPE_OPTIONS = [
  { label: 'PROSHIP', value: 'PROSHIP' },
  { label: 'PROSHIP_WITH_SHIPMENT_HISTORY', value: 'PROSHIP_WITH_SHIPMENT_HISTORY' },
  { label: 'PROSHIP_WITH_CUSTOMER_INFO', value: 'PROSHIP_WITH_CUSTOMER_INFO' },
  { label: 'INCREFF', value: 'INCREFF' },
  { label: 'LIMECHAT', value: 'LIMECHAT' },
  { label: 'WEBENGAGE', value: 'WEBENGAGE' },
];

// The fixed system webhook that must never be edited from these forms.
export const LOCKED_WEBHOOK_URL = 'https://channel-proship.prozo.com/api/order/status-update';
